import { Model3DMeshSet, Vec3Tuple } from "../types";

/** Helix, strand, coil — the codes labiq reads out of the entry's HELIX/SHEET records. */
export type SecondaryStructure = "H" | "E" | "C";

export type CartoonColorMode = "bFactor" | "chain" | "structure";

export interface ColorRamp {
	low: Vec3Tuple;
	mid: Vec3Tuple;
	high: Vec3Tuple;
}

/** Structurally satisfied by the columnar payload, declared here so the viewer imports no domain type. */
export interface CartoonAtomSource {
	chain: string[];
	resSeq: number[];
	name: string[];
	x: number[];
	y: number[];
	z: number[];
	bFactor: number[];
	ss?: string;
}

export interface CartoonResidue {
	chain: string;
	resSeq: number;
	ss: SecondaryStructure;
	ca: Vec3Tuple;
	o: Vec3Tuple | null;
	bFactor: number;
}

export interface CartoonSample {
	/** Residue-space parameter along the strip: 0 is its first residue, 1 its second. */
	u: number;
	position: Vec3Tuple;
	tangent: Vec3Tuple;
	width: Vec3Tuple;
	normal: Vec3Tuple;
	halfWidth: number;
	halfThickness: number;
	color: Vec3Tuple;
}

export interface CartoonOptions {
	colorMode?: CartoonColorMode;
	ramp?: ColorRamp;
	chainColors?: Vec3Tuple[];
	structureColors?: Record<SecondaryStructure, Vec3Tuple>;
	samplesPerResidue?: number;
	radialSegments?: number;
}

export interface CartoonAccumulator {
	positions: number[];
	normals: number[];
	colors: number[];
	indices: number[];
}

const HELIX_HALF_WIDTH = 1.1;
const SHEET_HALF_WIDTH = 1.15;
const RIBBON_HALF_THICKNESS = 0.2;
const COIL_RADIUS = 0.26;
const ARROW_FLARE = 1.8;
const ARROW_TIP = 0.12;

/** Consecutive alpha carbons sit near 3.8 A; anything wider is a break, not a bond. */
const CHAIN_BREAK_ANGSTROM = 5;

/** Without carbonyls there is no meaningful ribbon face, so every code degrades to a tube. */
const TUBE_RADIUS: Record<SecondaryStructure, number> = { H: 0.5, E: 0.45, C: 0.26 };

const DEFAULT_RAMP: ColorRamp = { low: [0.16, 0.5, 0.9], mid: [0.93, 0.93, 0.88], high: [0.86, 0.24, 0.2] };

const DEFAULT_CHAIN_COLORS: Vec3Tuple[] = [
	[0.24, 0.55, 0.9],
	[0.9, 0.6, 0.2],
	[0.35, 0.72, 0.42],
	[0.8, 0.35, 0.62],
	[0.55, 0.45, 0.85],
];

const DEFAULT_STRUCTURE_COLORS: Record<SecondaryStructure, Vec3Tuple> = {
	H: [0.86, 0.32, 0.3],
	E: [0.95, 0.78, 0.28],
	C: [0.62, 0.64, 0.68],
};

const EPSILON = 1e-6;

// ── vector helpers ──────────────────────────────────────────────────────────────

const subtract = (a: Vec3Tuple, b: Vec3Tuple): Vec3Tuple => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];

const scale = (a: Vec3Tuple, k: number): Vec3Tuple => [a[0] * k, a[1] * k, a[2] * k];

const dot = (a: Vec3Tuple, b: Vec3Tuple): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];

const cross = (a: Vec3Tuple, b: Vec3Tuple): Vec3Tuple => [
	a[1] * b[2] - a[2] * b[1],
	a[2] * b[0] - a[0] * b[2],
	a[0] * b[1] - a[1] * b[0],
];

const length = (a: Vec3Tuple): number => Math.sqrt(dot(a, a));

const normalize = (a: Vec3Tuple): Vec3Tuple => {
	const magnitude = length(a);
	return magnitude < EPSILON ? [0, 0, 0] : scale(a, 1 / magnitude);
};

export const distance = (a: Vec3Tuple, b: Vec3Tuple): number => length(subtract(a, b));

const mix = (a: Vec3Tuple, b: Vec3Tuple, t: number): Vec3Tuple => [
	a[0] + (b[0] - a[0]) * t,
	a[1] + (b[1] - a[1]) * t,
	a[2] + (b[2] - a[2]) * t,
];

const anyPerpendicular = (a: Vec3Tuple): Vec3Tuple => {
	const axis: Vec3Tuple = Math.abs(a[0]) < 0.9 ? [1, 0, 0] : [0, 1, 0];
	const candidate = normalize(cross(a, axis));
	return length(candidate) < EPSILON ? [0, 1, 0] : candidate;
};

/** Gram-Schmidt: keep the cross-section square to the path however the frame was derived. */
const orthonormalize = (vector: Vec3Tuple, axis: Vec3Tuple): Vec3Tuple => {
	const projected = subtract(vector, scale(axis, dot(vector, axis)));
	return length(projected) < EPSILON ? anyPerpendicular(axis) : normalize(projected);
};

// ── residues ────────────────────────────────────────────────────────────────────

const codeAt = (ss: string | undefined, index: number): SecondaryStructure => {
	const code = ss ? ss.charAt(index) : "";
	return code === "H" || code === "E" ? code : "C";
};

/** Collapse the columnar payload to one record per residue, keeping the alpha carbon and carbonyl. */
export const extractResidues = (source: CartoonAtomSource): CartoonResidue[] => {
	const entries: Array<{ residue: CartoonResidue; hasCa: boolean }> = [];
	const byKey = new Map<string, { residue: CartoonResidue; hasCa: boolean }>();

	for (let i = 0; i < source.x.length; i += 1) {
		const name = source.name[i];

		if (name !== "CA" && name !== "O") continue;

		const chain = source.chain[i];
		const resSeq = source.resSeq[i];
		const key = chain + " " + resSeq;
		let entry = byKey.get(key);

		if (!entry) {
			entry = {
				residue: { chain, resSeq, ss: codeAt(source.ss, i), ca: [0, 0, 0], o: null, bFactor: 0 },
				hasCa: false,
			};
			byKey.set(key, entry);
			entries.push(entry);
		}

		const position: Vec3Tuple = [source.x[i], source.y[i], source.z[i]];

		if (name === "CA") {
			entry.residue.ca = position;
			entry.residue.bFactor = source.bFactor[i];
			entry.residue.ss = codeAt(source.ss, i);
			entry.hasCa = true;
		} else {
			entry.residue.o = position;
		}
	}

	return entries.filter((entry) => entry.hasCa).map((entry) => entry.residue);
};

/** One continuous ribbon per run. A new chain or a coordinate gap starts a new one — never a bridge. */
export const splitStrips = (residues: CartoonResidue[]): CartoonResidue[][] => {
	const strips: CartoonResidue[][] = [];
	let current: CartoonResidue[] = [];

	for (let i = 0; i < residues.length; i += 1) {
		const residue = residues[i];
		const previous = current[current.length - 1];

		if (previous && (previous.chain !== residue.chain || distance(previous.ca, residue.ca) > CHAIN_BREAK_ANGSTROM)) {
			strips.push(current);
			current = [];
		}

		current.push(residue);
	}

	if (current.length) strips.push(current);

	return strips;
};

// ── spline ──────────────────────────────────────────────────────────────────────

/**
 * Reflect a phantom control point past each end rather than duplicating the endpoint: duplicating
 * halves the curve's speed at the first and last residue, which bunches samples at a chain terminus.
 */
const controlPoint = (points: Vec3Tuple[], index: number): Vec3Tuple => {
	const last = points.length - 1;

	if (index < 0) return reflect(points[0], points[Math.min(1, last)]);
	if (index > last) return reflect(points[last], points[Math.max(last - 1, 0)]);

	return points[index];
};

const reflect = (anchor: Vec3Tuple, neighbour: Vec3Tuple): Vec3Tuple => [
	2 * anchor[0] - neighbour[0],
	2 * anchor[1] - neighbour[1],
	2 * anchor[2] - neighbour[2],
];

/** Uniform Catmull-Rom. It interpolates its control points, so the path passes through every CA. */
export const catmullRomPoint = (points: Vec3Tuple[], segment: number, t: number): Vec3Tuple => {
	const p0 = controlPoint(points, segment - 1);
	const p1 = controlPoint(points, segment);
	const p2 = controlPoint(points, segment + 1);
	const p3 = controlPoint(points, segment + 2);
	const t2 = t * t;
	const t3 = t2 * t;

	const axis = (i: number) =>
		0.5 *
		(2 * p1[i] +
			(-p0[i] + p2[i]) * t +
			(2 * p0[i] - 5 * p1[i] + 4 * p2[i] - p3[i]) * t2 +
			(-p0[i] + 3 * p1[i] - 3 * p2[i] + p3[i]) * t3);

	return [axis(0), axis(1), axis(2)];
};

export const catmullRomTangent = (points: Vec3Tuple[], segment: number, t: number): Vec3Tuple => {
	const p0 = controlPoint(points, segment - 1);
	const p1 = controlPoint(points, segment);
	const p2 = controlPoint(points, segment + 1);
	const p3 = controlPoint(points, segment + 2);

	const axis = (i: number) =>
		0.5 *
		(-p0[i] +
			p2[i] +
			2 * (2 * p0[i] - 5 * p1[i] + 4 * p2[i] - p3[i]) * t +
			3 * (-p0[i] + 3 * p1[i] - 3 * p2[i] + p3[i]) * t * t);

	return [axis(0), axis(1), axis(2)];
};

// ── framing ─────────────────────────────────────────────────────────────────────

/**
 * The carbonyl vector is what makes a helix twist instead of tumble. Consecutive carbonyls point
 * roughly opposite ways, so each is flipped back onto its predecessor before it is used.
 */
export const orientationDirections = (strip: CartoonResidue[]): Vec3Tuple[] | null => {
	for (let i = 0; i < strip.length; i += 1) {
		if (!strip[i].o) return null;
	}

	const directions: Vec3Tuple[] = [];

	for (let i = 0; i < strip.length; i += 1) {
		let direction = normalize(subtract(strip[i].o as Vec3Tuple, strip[i].ca));

		if (length(direction) < EPSILON) direction = [1, 0, 0];
		if (i > 0 && dot(direction, directions[i - 1]) < 0) direction = scale(direction, -1);

		directions[i] = direction;
	}

	return directions;
};

export interface StripProfile {
	halfWidth: number[];
	halfThickness: number[];
}

/** The flare sits on the last strand residue, so the taper into what follows IS the arrow point. */
const applyArrowheads = (strip: CartoonResidue[], halfWidth: number[]): void => {
	for (let i = 0; i < strip.length; i += 1) {
		if (strip[i].ss !== "E") continue;

		let end = i;
		while (end + 1 < strip.length && strip[end + 1].ss === "E") end += 1;

		if (end + 1 < strip.length) {
			halfWidth[end] = SHEET_HALF_WIDTH * ARROW_FLARE;
		} else {
			halfWidth[end] = ARROW_TIP;
			if (end - 1 >= i) halfWidth[end - 1] = SHEET_HALF_WIDTH * ARROW_FLARE;
		}

		i = end;
	}
};

export const stripProfile = (strip: CartoonResidue[], flat: boolean): StripProfile => {
	const halfWidth: number[] = [];
	const halfThickness: number[] = [];

	for (let i = 0; i < strip.length; i += 1) {
		const ss = strip[i].ss;

		if (!flat) {
			halfWidth[i] = TUBE_RADIUS[ss];
			halfThickness[i] = TUBE_RADIUS[ss];
		} else if (ss === "H") {
			halfWidth[i] = HELIX_HALF_WIDTH;
			halfThickness[i] = RIBBON_HALF_THICKNESS;
		} else if (ss === "E") {
			halfWidth[i] = SHEET_HALF_WIDTH;
			halfThickness[i] = RIBBON_HALF_THICKNESS;
		} else {
			halfWidth[i] = COIL_RADIUS;
			halfThickness[i] = COIL_RADIUS;
		}
	}

	if (flat) applyArrowheads(strip, halfWidth);

	return { halfWidth, halfThickness };
};

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

export const buildStripSamples = (
	strip: CartoonResidue[],
	colors: Vec3Tuple[],
	options: CartoonOptions = {}
): CartoonSample[] => {
	if (strip.length < 2) return [];

	const points = strip.map((residue) => residue.ca);
	const directions = orientationDirections(strip);
	const profile = stripProfile(strip, directions !== null);
	const perResidue = Math.max(1, Math.floor(options.samplesPerResidue ?? 5));
	const last = strip.length - 1;
	const samples: CartoonSample[] = [];

	let carried: Vec3Tuple | null = null;

	const emit = (segment: number, t: number) => {
		const next = Math.min(segment + 1, last);
		const position = catmullRomPoint(points, segment, t);
		const derivative = catmullRomTangent(points, segment, t);
		const tangent: Vec3Tuple = length(derivative) < EPSILON ? [0, 0, 1] : normalize(derivative);

		let seed: Vec3Tuple;
		if (directions) seed = mix(directions[segment], directions[next], t);
		else if (carried) seed = carried;
		else seed = anyPerpendicular(tangent);

		const width = orthonormalize(seed, tangent);
		carried = width;

		samples.push({
			u: segment + t,
			position,
			tangent,
			width,
			normal: normalize(cross(tangent, width)),
			halfWidth: lerp(profile.halfWidth[segment], profile.halfWidth[next], t),
			halfThickness: lerp(profile.halfThickness[segment], profile.halfThickness[next], t),
			color: mix(colors[segment], colors[next], t),
		});
	};

	for (let segment = 0; segment < last; segment += 1) {
		for (let step = 0; step < perResidue; step += 1) {
			emit(segment, step / perResidue);
		}
	}

	emit(last - 1, 1);

	return samples;
};

// ── geometry ────────────────────────────────────────────────────────────────────

const ringPoint = (sample: CartoonSample, angle: number): Vec3Tuple => {
	const u = Math.cos(angle) * sample.halfWidth;
	const v = Math.sin(angle) * sample.halfThickness;

	return [
		sample.position[0] + sample.width[0] * u + sample.normal[0] * v,
		sample.position[1] + sample.width[1] * u + sample.normal[1] * v,
		sample.position[2] + sample.width[2] * u + sample.normal[2] * v,
	];
};

const ringNormal = (sample: CartoonSample, angle: number): Vec3Tuple => {
	const u = Math.cos(angle) / Math.max(sample.halfWidth, EPSILON);
	const v = Math.sin(angle) / Math.max(sample.halfThickness, EPSILON);

	return normalize([
		sample.width[0] * u + sample.normal[0] * v,
		sample.width[1] * u + sample.normal[1] * v,
		sample.width[2] * u + sample.normal[2] * v,
	]);
};

const pushVertex = (target: CartoonAccumulator, position: Vec3Tuple, normal: Vec3Tuple, color: Vec3Tuple): void => {
	target.positions.push(position[0], position[1], position[2]);
	target.normals.push(normal[0], normal[1], normal[2]);
	target.colors.push(color[0], color[1], color[2]);
};

const appendCap = (
	target: CartoonAccumulator,
	sample: CartoonSample,
	radialSegments: number,
	isStart: boolean
): void => {
	const facing = isStart ? scale(sample.tangent, -1) : sample.tangent;
	const centre = target.positions.length / 3;

	pushVertex(target, sample.position, facing, sample.color);

	for (let k = 0; k < radialSegments; k += 1) {
		pushVertex(target, ringPoint(sample, (k / radialSegments) * Math.PI * 2), facing, sample.color);
	}

	for (let k = 0; k < radialSegments; k += 1) {
		const here = centre + 1 + k;
		const next = centre + 1 + ((k + 1) % radialSegments);
		if (isStart) target.indices.push(centre, next, here);
		else target.indices.push(centre, here, next);
	}
};

export const appendStrip = (target: CartoonAccumulator, samples: CartoonSample[], radialSegments: number): void => {
	if (samples.length < 2) return;

	const base = target.positions.length / 3;

	for (let s = 0; s < samples.length; s += 1) {
		for (let k = 0; k < radialSegments; k += 1) {
			const angle = (k / radialSegments) * Math.PI * 2;
			pushVertex(target, ringPoint(samples[s], angle), ringNormal(samples[s], angle), samples[s].color);
		}
	}

	for (let s = 0; s + 1 < samples.length; s += 1) {
		const here = base + s * radialSegments;
		const next = base + (s + 1) * radialSegments;

		for (let k = 0; k < radialSegments; k += 1) {
			const k1 = (k + 1) % radialSegments;
			target.indices.push(here + k, here + k1, next + k, here + k1, next + k1, next + k);
		}
	}

	appendCap(target, samples[0], radialSegments, true);
	appendCap(target, samples[samples.length - 1], radialSegments, false);
};

// ── colour ──────────────────────────────────────────────────────────────────────

export const bFactorRange = (residues: CartoonResidue[]): { min: number; max: number } => {
	let min = Infinity;
	let max = -Infinity;

	for (let i = 0; i < residues.length; i += 1) {
		const value = residues[i].bFactor;
		if (value < min) min = value;
		if (value > max) max = value;
	}

	return Number.isFinite(min) ? { min, max } : { min: 0, max: 0 };
};

export const rampColor = (value: number, min: number, max: number, ramp: ColorRamp = DEFAULT_RAMP): Vec3Tuple => {
	if (!(max > min)) return ramp.mid;

	const t = Math.min(Math.max((value - min) / (max - min), 0), 1);

	return t < 0.5 ? mix(ramp.low, ramp.mid, t * 2) : mix(ramp.mid, ramp.high, (t - 0.5) * 2);
};

const residueColor = (
	residue: CartoonResidue,
	range: { min: number; max: number },
	chainIndex: number,
	options: CartoonOptions
): Vec3Tuple => {
	if (options.colorMode === "chain") {
		const palette = options.chainColors && options.chainColors.length ? options.chainColors : DEFAULT_CHAIN_COLORS;
		return palette[chainIndex % palette.length];
	}

	if (options.colorMode === "structure") {
		return (options.structureColors ?? DEFAULT_STRUCTURE_COLORS)[residue.ss];
	}

	return rampColor(residue.bFactor, range.min, range.max, options.ramp ?? DEFAULT_RAMP);
};

// ── entry point ─────────────────────────────────────────────────────────────────

/** One merged mesh per chain. A mesh per residue would be hundreds of draw calls on one rotation. */
export const buildCartoonMeshes = (residues: CartoonResidue[], options: CartoonOptions = {}): Model3DMeshSet[] => {
	const radialSegments = Math.max(3, Math.floor(options.radialSegments ?? 8));
	const range = bFactorRange(residues);
	const order: string[] = [];
	const byChain = new Map<string, CartoonAccumulator>();
	const strips = splitStrips(residues);

	for (let i = 0; i < strips.length; i += 1) {
		const strip = strips[i];
		if (strip.length < 2) continue;

		const chain = strip[0].chain;
		let target = byChain.get(chain);

		if (!target) {
			target = { positions: [], normals: [], colors: [], indices: [] };
			byChain.set(chain, target);
			order.push(chain);
		}

		const chainIndex = order.indexOf(chain);
		const colors = strip.map((residue) => residueColor(residue, range, chainIndex, options));

		appendStrip(target, buildStripSamples(strip, colors, options), radialSegments);
	}

	return order
		.map((chain, index) => {
			const target = byChain.get(chain) as CartoonAccumulator;
			return {
				id: "cartoon-" + index + "-" + chain,
				positions: target.positions,
				normals: target.normals,
				colors: target.colors,
				indices: target.indices,
			};
		})
		.filter((set) => set.indices.length > 0);
};

export const buildCartoonModel = (source: CartoonAtomSource, options: CartoonOptions = {}): Model3DMeshSet[] =>
	buildCartoonMeshes(extractResidues(source), options);
